import { StompSubscription } from "@stomp/stompjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import {
  ChangeUtilityBillsStatusesRequestDTO,
  UtilityBillByIdDTO,
  UtilityBillsDTO,
  UtilityBillsQuery,
  changeUtilityBillsStatuses,
  getSavedSearchFilters,
  getUtilityBillById,
  getUtilityBills,
  getUtilityBillsYears,
} from "api";
import { useNotify } from "hooks";
import { extractErrorMessage } from "utils";

import { QUERY_KEYS } from "consts";

import { DOCUMENTS_SUMMARY_REFETCH_INTERVAL } from "./consts";
import { OptionalRefetchIntervalAndRetry } from "./types";

import { useWebSocketClient } from "../websocket";

export const useUtilityBills = ({
  refetchInterval = DOCUMENTS_SUMMARY_REFETCH_INTERVAL,
  retry = false,
  ...query
}: UtilityBillsQuery & OptionalRefetchIntervalAndRetry) =>
  useQuery<UtilityBillsDTO>(
    [QUERY_KEYS.UTILITY_BILLS, query],
    () => getUtilityBills(query),
    {
      refetchInterval,
      retry,
    },
  );

export const useUtilityBillById = (id: string) =>
  useQuery<UtilityBillByIdDTO>(
    [QUERY_KEYS.UTILITY_BILLS, id],
    () => getUtilityBillById(id),
    {
      enabled: !!id, // Only fetch if id is provided and not empty
      retry: false, // Don't retry on error to avoid unnecessary requests
    }
  );

export const useGetSavedSearchFilters = () =>
  useQuery<any>([QUERY_KEYS.UTILITY_BILLS], () => getSavedSearchFilters());

export const useUtilityBillsYears = () =>
  useQuery<number[]>([QUERY_KEYS.UTILITY_BILLS, "years"], getUtilityBillsYears);

export const useBulkUtilityBillsStatuses = (onAfterSuccess?: () => void) => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (body: ChangeUtilityBillsStatusesRequestDTO) => changeUtilityBillsStatuses(body),
    onSuccess: (data: any) => {
      const updatedCount = data?.updatedCount ?? 0;
      const failedCount = data?.failedCount ?? 0;

      if (updatedCount > 0) {
        notify.success(`${updatedCount} utility bill(s) updated successfully!`);
      }

      if (failedCount > 0) {
        notify.error(`Unable to update the status for ${failedCount} data entries. Please review for missing required fields or invalid status transitions, then try again.`);
      }

      queryClient.invalidateQueries([QUERY_KEYS.DOCUMENT_BILLS]);
      onAfterSuccess?.();
    },
    onError: (error: unknown) => {
      notify.error(extractErrorMessage(error, "Failed to update utility bills status"));
    },
  });

  return {
    applyBulkStatuses: mutateAsync,
    isApplying: isLoading,
  };
};

export const useUtilityBillWSConnection = (utilityBillId: string) => {
  const wsClient = useWebSocketClient();
  const subscription = useRef<StompSubscription | null>();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only subscribe if utilityBillId is provided and WebSocket is connected
    if (wsClient.connected && utilityBillId) {
      subscription.current = wsClient.subscribe(
        `/topic/bill/${utilityBillId}`,
        (payload) => {
          try {
            const utilityBill = JSON.parse(payload.body);
            queryClient.setQueryData(
              [QUERY_KEYS.UTILITY_BILLS, utilityBillId],
              utilityBill,
            );
          } catch (e) {
            console.error(
              "useUtilityBillWSConnection: Could not parse payload from ws!",
            );
          }
        },
      );
    }

    return () => {
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
    };
  }, [utilityBillId, wsClient, queryClient, wsClient.connected]);
};
